import { useState } from "react";
import type { FormField } from "./types";
import type { Strings } from "./strings";
import { ChevronDownIcon } from "./icons";

/**
 * One input per field type, driven entirely by the snapshot the server sent.
 *
 * Validation is intentionally thin here — the server runs the real check in
 * lib/formDomain.ts and returns per-field reasons, which this renders. Doing it
 * only in the browser would be a suggestion, not a rule.
 */

export type Answers = Record<string, unknown>;

function labelFor(field: FormField, locale: "en" | "ar"): string {
  return locale === "ar" ? (field.labelAr ?? field.label) : field.label;
}

function optionLabel(
  option: { label: string; labelAr?: string },
  locale: "en" | "ar",
): string {
  return locale === "ar" ? (option.labelAr ?? option.label) : option.label;
}

function Field({
  field,
  locale,
  strings,
  value,
  error,
  disabled,
  onChange,
}: {
  field: FormField;
  locale: "en" | "ar";
  strings: Strings;
  value: unknown;
  error: string | null;
  disabled: boolean;
  onChange: (next: unknown) => void;
}) {
  const id = `field-${field.key}`;
  const described = [
    field.helpText ? `${id}-help` : null,
    error ? `${id}-error` : null,
  ]
    .filter(Boolean)
    .join(" ");

  const common = {
    id,
    disabled,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": described || undefined,
    className: "field-input",
  };

  let control: React.ReactNode;
  switch (field.inputType) {
    case "long_text":
      control = (
        <textarea
          {...common}
          rows={4}
          value={String(value ?? "")}
          onChange={(event) => onChange(event.target.value)}
        />
      );
      break;
    case "checkbox":
      control = (
        <label className="checkbox-row">
          <input
            {...common}
            className="field-checkbox"
            type="checkbox"
            checked={value === true}
            onChange={(event) => onChange(event.target.checked)}
          />
          <span>{strings.yes}</span>
        </label>
      );
      break;
    case "single_select":
      control = (
        <div className="select-wrap">
          <select
            {...common}
            value={String(value ?? "")}
            onChange={(event) => onChange(event.target.value)}
          >
            <option value="">{strings.choose}</option>
            {(field.options ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {optionLabel(option, locale)}
              </option>
            ))}
          </select>
          <ChevronDownIcon className="select-caret" />
        </div>
      );
      break;
    case "multi_select": {
      const selected = Array.isArray(value) ? value.map(String) : [];
      control = (
        <div className="checkbox-group" id={id}>
          {(field.options ?? []).map((option) => (
            <label key={option.value} className="checkbox-row">
              <input
                type="checkbox"
                className="field-checkbox"
                disabled={disabled}
                checked={selected.includes(option.value)}
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...selected, option.value]
                      : selected.filter((entry) => entry !== option.value),
                  )
                }
              />
              <span>{optionLabel(option, locale)}</span>
            </label>
          ))}
        </div>
      );
      break;
    }
    case "date":
    case "datetime":
      control = (
        <input
          {...common}
          type={field.inputType === "date" ? "date" : "datetime-local"}
          value={String(value ?? "")}
          onChange={(event) => onChange(event.target.value)}
        />
      );
      break;
    case "integer":
    case "decimal":
    case "money":
      control = (
        <input
          {...common}
          type="number"
          inputMode={field.inputType === "integer" ? "numeric" : "decimal"}
          step={field.inputType === "integer" ? 1 : "any"}
          value={String(value ?? "")}
          onChange={(event) => onChange(event.target.value)}
        />
      );
      break;
    default: {
      // Using the matching input type is what gets a phone keypad on a phone,
      // which is most of where these forms are opened.
      const type =
        field.inputType === "email"
          ? "email"
          : field.inputType === "phone"
            ? "tel"
            : field.inputType === "url"
              ? "url"
              : "text";
      control = (
        <input
          {...common}
          type={type}
          inputMode={field.inputType === "phone" ? "tel" : undefined}
          value={String(value ?? "")}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    }
  }

  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {labelFor(field, locale)}
        {field.required ? (
          <span className="field-required" aria-hidden>
            *
          </span>
        ) : (
          <span className="field-optional">{strings.optional}</span>
        )}
      </label>
      {field.helpText ? (
        <p className="field-help" id={`${id}-help`}>
          {field.helpText}
        </p>
      ) : null}
      {control}
      {error ? (
        <p className="field-error" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function FormRenderer({
  fields,
  locale,
  strings,
  initial,
  disabled,
  submitting,
  submitLabel,
  errors,
  onSubmit,
}: {
  fields: FormField[];
  locale: "en" | "ar";
  strings: Strings;
  initial: Answers;
  disabled: boolean;
  submitting: boolean;
  submitLabel: string;
  errors: Record<string, string>;
  onSubmit: (answers: Answers) => void;
}) {
  const [answers, setAnswers] = useState<Answers>(initial);

  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!disabled && !submitting) onSubmit(answers);
      }}
      // noValidate so the server's messages are the only ones shown; browser
      // bubbles would say something different in a third language.
      noValidate
    >
      {fields.map((field) => (
        <Field
          key={field.key}
          field={field}
          locale={locale}
          strings={strings}
          value={answers[field.key]}
          error={errors[field.key] ?? null}
          disabled={disabled || submitting}
          onChange={(next) =>
            setAnswers((current) => ({ ...current, [field.key]: next }))
          }
        />
      ))}
      <button className="submit" type="submit" disabled={disabled || submitting}>
        {submitting ? (
          <>
            <span className="spinner" aria-hidden />
            {strings.submitting}
          </>
        ) : (
          submitLabel
        )}
      </button>
    </form>
  );
}
